import { Component } from 'react';
import * as React from 'react';
import { TBreakpoint as Breakpoint } from '../App';
import { Button } from 'reactstrap';
import { copy } from '../ClipboardUtils';
import SyntaxHighlighter from "react-syntax-highlighter";

interface Props {
    breakpoints: Breakpoint[];
    responsive: boolean;
    backgroundImage: boolean;
    onSnippetChange?: (snippet: string) => void;
    altText: string;
    lazy: boolean;
    apiKey: string;
}

interface Op {
    op: string;
    params?: string;
}

class PixboostJsSnippet extends Component<Props, {}> {
    static op(b: Breakpoint): Op {
        if (b.hide) {
            return {op: 'hide'};
        } else if (b.asis) {
            return {op: 'asis'};
        } else if (b.width && b.height) {
            return {op: 'fit', params: `size=${b.width}x${b.height}`};
        } else if (b.width || b.height) {
            return {op: 'resize', params: `size=${b.width ? b.width.toString(10) : 'x' + b.height}`};
        }

        return {op: 'optimise'};
    }

    static generateBreakpointAttrs(b: Breakpoint, addUrl: boolean): string {
        if (!b) {
            return '';
        }

        const prefix = `data-${b.name.toLowerCase()}`;
        const op = PixboostJsSnippet.op(b);
        const escapedUrl = PixboostJsSnippet.escapeImageUrl(b.url);

        let breakpointAttrs = `  `;

        if (addUrl) {
            breakpointAttrs += `${prefix}-url="${escapedUrl}"\n`;
        }
        breakpointAttrs += `  ${prefix}="${op.op}${op.params ? `?${op.params}` : ''}"`;

        return breakpointAttrs;
    }

    static escapeImageUrl(url: string): string {
        return url.replace(/\?/, '%3F');
    }

    generateSnippet() {
        let snippet: string;

        if (this.props.responsive) {
            snippet = this.generateResponsiveSnippet();
        } else {
            snippet = this.generateImgSnippet();
        }
        if (this.props.onSnippetChange) {
            this.props.onSnippetChange(snippet);
        }
        return snippet;
    }

    generateResponsiveSnippet(): string {
        const {breakpoints} = this.props;

        const useDefaultUrl = breakpoints.length === 3 &&
            breakpoints[0].url === breakpoints[1].url &&
            breakpoints[0].url === breakpoints[2].url;
        const isLazy = this.props.lazy;
        const hasAltText = this.props.altText.length > 0;
        const type = this.props.backgroundImage ? 'background' : 'picture';

        let snippet =
            `<div data-pb-${type} ` +
            `${useDefaultUrl ? `\n    data-url="${PixboostJsSnippet.escapeImageUrl(breakpoints[0].url)}"` : ''}`;
        if (isLazy) {
            snippet += '\n    data-lazy=""';
        }
        if (hasAltText) {
            snippet += `\n    data-alt="${this.props.altText}"`;
        }
        snippet += `\n${PixboostJsSnippet.generateBreakpointAttrs(this.props.breakpoints[0], !useDefaultUrl)}`;
        snippet += `\n${PixboostJsSnippet.generateBreakpointAttrs(this.props.breakpoints[1], !useDefaultUrl)}`;
        snippet += `\n${PixboostJsSnippet.generateBreakpointAttrs(this.props.breakpoints[2], !useDefaultUrl)}`;
        snippet += '\n></div>';
        return snippet;
    }

    generateImgSnippet(): string {
        const bp = this.props.breakpoints[0];
        const op = PixboostJsSnippet.op(bp);
        const isLazy = this.props.lazy;
        const hasAltText = this.props.altText.length > 0;
        const type = this.props.backgroundImage ? 'background' : 'image';

        let snippet = `<${type === 'image' ? 'img' : 'div'} data-pb-${type}`;
        if (isLazy) {
            snippet += '\n    data-lazy=""';
        }
        if (hasAltText && type === 'image') {
            snippet += `\n    alt="${this.props.altText}"`;
        }
        snippet += `\n    data-op="${op.op}${op.params ? `?${op.params}` : ''}"`;
        snippet += `\n    data-${type === 'image' ? 'src' : 'url'}="${PixboostJsSnippet.escapeImageUrl(bp.url)}"/>`;

        return snippet;
    }

    render() {
        return (
            <div>
                <div style={{backgroundColor: 'lightyellow', padding: 10}}>
                    <SyntaxHighlighter language="html" customStyle={{backgroundColor: 'lightyellow'}}>
                        {this.generateSnippet()}
                    </SyntaxHighlighter>
                    <div style={{width: '100%', textAlign: 'right'}}>
                        <div className={'pb-2'}>
                            <Button color={'primary'} onClick={() => { copy(this.generateSnippet()); }}>Copy</Button>
                        </div>
                    </div>
                </div>


                <small>
                    To start using pixboost.js, copy code below to the &lt;head&gt; element of your page:

                    <SyntaxHighlighter language="html">
                    {
                        `<script id="pb-script" data-api-key="${this.props.apiKey}" 
    data-autoload="" src="https://pixboost.com/libs/pixboost.bundle.min.js" async=""></script>`
                    }
                    </SyntaxHighlighter>
                </small>
            </div>
        );
    }
}

export default PixboostJsSnippet;